import { PointerEvent as ReactPointerEvent, useEffect, useRef } from 'react';
import type { PlaneFinderApp } from '../app/PlaneFinderApp';
import type { RectHandle } from '../app/interaction/RectController';

type Pt = [number, number];
const mid = (a: Pt, b: Pt): Pt => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
const normalize = (v: Pt): Pt => {
  const l = Math.hypot(v[0], v[1]) || 1;
  return [v[0] / l, v[1] / l];
};

/**
 * The adjustable capture rect. The SVG geometry is reprojected imperatively every
 * frame (the rect lives in plane space; corners are projected to screen by the
 * app), so the React tree never re-renders during orbit/drag. Handles drive the
 * RectController via canvas-relative CSS coordinates.
 */
export function RectOverlay({ app, active }: { app: PlaneFinderApp; active: boolean }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const polyRef = useRef<SVGPolygonElement>(null);
  const rotLineRef = useRef<SVGLineElement>(null);
  const handles = useRef<Record<string, SVGCircleElement | null>>({});
  const dragging = useRef<RectHandle | null>(null);

  // Per-frame reprojection.
  useEffect(() => {
    if (!active) return;
    const setC = (el: SVGCircleElement | null, p: Pt) => {
      el?.setAttribute('cx', String(p[0]));
      el?.setAttribute('cy', String(p[1]));
    };
    const update = () => {
      const svg = svgRef.current;
      const corners = app.getOverlayCorners();
      if (!svg) return;
      if (!corners) {
        svg.style.opacity = '0';
        return;
      }
      svg.style.opacity = '1';
      const [bl, br, tr, tl] = corners as [Pt, Pt, Pt, Pt];
      polyRef.current?.setAttribute('points', corners.map((c) => c.join(',')).join(' '));
      setC(handles.current.bottom, mid(bl, br));
      setC(handles.current.right, mid(br, tr));
      setC(handles.current.top, mid(tr, tl));
      setC(handles.current.left, mid(tl, bl));
      setC(handles.current['bottom-left'], bl);
      setC(handles.current['bottom-right'], br);
      setC(handles.current['top-right'], tr);
      setC(handles.current['top-left'], tl);
      const center: Pt = mid(bl, tr);
      setC(handles.current.move, center);
      const topMid = mid(tr, tl);
      const dir = normalize([topMid[0] - center[0], topMid[1] - center[1]]);
      const rot: Pt = [topMid[0] + dir[0] * 34, topMid[1] + dir[1] * 34];
      rotLineRef.current?.setAttribute('x1', String(topMid[0]));
      rotLineRef.current?.setAttribute('y1', String(topMid[1]));
      rotLineRef.current?.setAttribute('x2', String(rot[0]));
      rotLineRef.current?.setAttribute('y2', String(rot[1]));
      setC(handles.current.rotate, rot);
    };
    const off = app.addFrameObserver(update);
    update();
    return off;
  }, [app, active]);

  // Window-level drag (pointer can leave the small handle).
  useEffect(() => {
    if (!active) return;
    const cssOf = (e: PointerEvent): Pt => {
      const r = svgRef.current!.getBoundingClientRect();
      return [e.clientX - r.left, e.clientY - r.top];
    };
    const onMove = (e: PointerEvent) => {
      if (dragging.current) {
        const [x, y] = cssOf(e);
        app.dragRect(dragging.current, x, y);
      }
    };
    const onUp = () => {
      dragging.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    // pointercancel: the browser can take over a touch gesture (scroll/zoom) and
    // never send pointerup — end the drag the same way or it stays stuck.
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [app, active]);

  if (!active) return null;

  const start = (handle: RectHandle) => (e: ReactPointerEvent) => {
    e.stopPropagation();
    const r = svgRef.current!.getBoundingClientRect();
    dragging.current = handle;
    app.beginRectDrag(handle, e.clientX - r.left, e.clientY - r.top);
  };

  return (
    <svg ref={svgRef} className="ej-rect-overlay" style={{ opacity: 0 }}>
      <polygon ref={polyRef} className="ej-rect-poly" points="" />
      <line ref={rotLineRef} className="ej-rect-rotline" />
      {(['left', 'right', 'top', 'bottom'] as const).map((side) => (
        <circle
          key={side}
          ref={(el) => (handles.current[side] = el)}
          className="ej-rect-handle"
          r={9}
          onPointerDown={start(side)}
        />
      ))}
      {(['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const).map((corner) => (
        <circle
          key={corner}
          ref={(el) => (handles.current[corner] = el)}
          className="ej-rect-handle"
          r={9}
          onPointerDown={start(corner)}
        />
      ))}
      <circle ref={(el) => (handles.current.move = el)} className="ej-rect-move" r={11} onPointerDown={start('move')} />
      <circle
        ref={(el) => (handles.current.rotate = el)}
        className="ej-rect-rotate"
        r={10}
        onPointerDown={start('rotate')}
      />
    </svg>
  );
}
